
import React, { useState, useEffect } from 'react';
import { getDatabase, ref, set, onValue, off } from 'firebase/database';
import { getAuth } from 'firebase/auth';

const CBT = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('First Term');
  const [selectedSession, setSelectedSession] = useState(`${new Date().getFullYear()}/${new Date().getFullYear() + 1}`);
  const [duration, setDuration] = useState(60);
  const [subjects, setSubjects] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState({ A: '', B: '', C: '', D: '' });
  const [correctAnswer, setCorrectAnswer] = useState('');
  const db = getDatabase();

  useEffect(() => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsLoading(false);
      return;
    }

    const userRef = ref(db, `users/${currentUser.uid}`);
    const callback = (snapshot) => {
      setUser(snapshot.val());
      setIsLoading(false);
    };
    onValue(userRef, callback);
    return () => off(userRef, 'value', callback);
  }, [db]);

  useEffect(() => {
    if (!user) return;
    const role = user.role?.toLowerCase();
    
    if (role === 'admin' || role === 'principal') {
        const classesRef = ref(db, 'classes');
        const callback = (snapshot) => {
            setAllClasses(snapshot.exists() ? Object.keys(snapshot.val()).sort() : []);
        };
        onValue(classesRef, callback);
        return () => off(classesRef, 'value', callback);
    } else if (role === 'teacher' || role === 'form-master') {
        // Allow teachers/form-masters to see all their assigned classes
        const assigned = user.assignedClass || (Array.isArray(user.assignedClasses) ? user.assignedClasses : []);
        const classesToShow = Array.isArray(assigned) ? assigned : [assigned];
        setAllClasses(classesToShow.sort());
        if (classesToShow.length === 1) {
            setSelectedClass(classesToShow[0]);
        }
    }
  }, [user, db]);

  useEffect(() => {
    if (!user) return;
    const role = user.role?.toLowerCase();

    if ((role === 'admin' || role === 'principal') && selectedClass) {
        const subjectsRef = ref(db, `classSubjects/${selectedClass}`);
        const callback = (snapshot) => {
            const val = snapshot.val();
            setSubjects(Array.isArray(val) ? val : Object.keys(val || {}));
        };
        onValue(subjectsRef, callback);
        return () => off(subjectsRef, 'value', callback);
    } else if (role === 'teacher' || role === 'form-master') {
        // Teachers/Form-masters can only set questions for subjects they are assigned
        setSubjects(user.assignedSubjects || []);
    }
  }, [user, selectedClass, db]);

  const clearForm = () => {
    setQuestion('');
    setOptions({ A: '', B: '', C: '', D: '' });
    setCorrectAnswer('');
  };

  const handleAddQuestion = () => {
    if (!question || !options.A || !options.B || !correctAnswer) {
      alert('Please fill in the question, at least two options, and the correct answer.');
      return;
    }
    const newQuestion = { id: Date.now(), question, options, correctAnswer };
    setPendingQuestions([...pendingQuestions, newQuestion]);
    clearForm();
  };

  const handleUploadToDatabase = async () => {
    if (pendingQuestions.length === 0) {
      alert('No questions to upload.');
      return;
    }
    if (!selectedSession || !selectedTerm || !selectedClass || !selectedSubject) {
      alert('Please select the session, term, class, and subject for the exam.');
      return;
    }

    const examPath = `exams/${selectedSession.replace('/', '-')}/${selectedTerm}/${selectedClass}/${selectedSubject}`;
    const examRef = ref(db, examPath);

    try {
      await set(examRef, {
        subject: selectedSubject,
        class: selectedClass,
        session: selectedSession,
        term: selectedTerm,
        duration: duration * 60,
        questionCount: pendingQuestions.length,
        createdBy: user.uid,
        questions: pendingQuestions.reduce((obj, q) => ({ ...obj, [q.id]: q }), {})
      });

      setPendingQuestions([]);
      alert('Exam uploaded successfully!');
    } catch (error) {
      console.error("Firebase upload error:", error);
      alert('An error occurred during upload. Please try again.');
    }
  };

  const sessions = Array.from({ length: 50 }, (_, i) => {
    const startYear = new Date().getFullYear() - i;
    return `${startYear}/${startYear + 1}`;
  });

  const terms = ['First Term', 'Second Term', 'Third Term'];

  if (isLoading) {
    return <div className="min-h-screen flex justify-center items-center"><p>Loading portal...</p></div>;
  }

  const authorizedRoles = ['teacher', 'form-master', 'admin', 'principal'];
  if (!user || !authorizedRoles.includes(user.role?.toLowerCase())) {
    return (
        <div className="min-h-screen flex justify-center items-center">
            <p className="text-red-500 font-bold">Access Restricted. Only authorized staff can access this portal.</p>
        </div>
    );
  }

  const canSelectClass = user && ['admin', 'principal', 'teacher', 'form-master'].includes(user.role?.toLowerCase());

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex justify-between items-center bg-green-600 text-white p-4">
        <h2 className="text-lg font-bold">Teacher Question Entry Portal</h2>
        <div className="text-right"><p>Teacher: {user.fullName || user.name} ({user.role})</p></div>
      </div>
      <div className="flex flex-1 p-4 space-x-4">
        <div className="w-3/4 bg-white p-6 shadow-md rounded-lg">
          <h3 className="text-lg font-semibold mb-4 text-green-700">Exam Details & Question Creation</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 pb-4 border-b">
            <div>
              <label className="block text-sm font-medium mb-1">Session:</label>
              <select className="w-full border rounded px-3 py-2" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)}>
                {sessions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Term:</label>
              <select className="w-full border rounded px-3 py-2" value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)}>
                {terms.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Class:</label>
              <select 
                className="w-full border rounded px-3 py-2" 
                value={selectedClass} 
                onChange={(e) => setSelectedClass(e.target.value)} 
                disabled={!canSelectClass || allClasses.length === 0}
              >
                <option value="">{allClasses.length > 0 ? '-- Select Class --' : 'No classes available'}</option>
                {allClasses.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subject:</label>
              <select 
                className="w-full border rounded px-3 py-2" 
                value={selectedSubject} 
                onChange={(e) => setSelectedSubject(e.target.value)} 
                disabled={subjects.length === 0}
              >
                <option value="">{subjects.length > 0 ? 'Select subject' : 'No subjects available'}</option>
                {subjects.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duration (Mins):</label>
              <input type="number" className="w-full border rounded px-3 py-2" value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))} />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Question:</label>
            <textarea value={question} onChange={e => setQuestion(e.target.value)} className="w-full border rounded px-3 py-2 h-24" placeholder="Enter question..."></textarea>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {Object.keys(options).map(key => (
              <div key={key}>
                <label className="block text-sm mb-1">Option {key}:</label>
                <input type="text" value={options[key]} onChange={e => setOptions({ ...options, [key]: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
            ))}
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Correct Answer:</label>
            <select value={correctAnswer} onChange={e => setCorrectAnswer(e.target.value)} className="w-1/4 border rounded px-3 py-2">
              <option value="">Select</option>
              {Object.keys(options).map(key => <option key={key} value={key}>{key}</option>)}
            </select>
          </div>
          <div className="flex justify-end">
            <button onClick={handleAddQuestion} className="bg-green-600 text-white px-6 py-2 rounded font-bold">Add Question</button>
          </div>
        </div>
        <div className="w-1/4 bg-gray-100 p-4 rounded-lg flex flex-col">
          <h4 className="text-lg font-semibold text-green-700 mb-3">Pending Questions ({pendingQuestions.length})</h4>
          <div className="flex-grow overflow-y-auto space-y-2 text-sm">
            {pendingQuestions.map((q, i) => (<div key={q.id} className="bg-white p-2 rounded shadow-sm"><strong>{i + 1}.</strong> {q.question.substring(0, 40)}...</div>))}
          </div>
          <button onClick={handleUploadToDatabase} className="mt-4 w-full bg-blue-500 text-white py-3 rounded font-bold">Upload Exam</button>
        </div>
      </div>
      <footer className="bg-green-700 text-white text-center py-2">© 2025 Holy See Academy</footer>
    </div>
  );
};

export default CBT;
